import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
    usePartnerDashboardSections, programRowsFrom, usePartnerWorkspace, usageByQuarterFrom,
} from './partnerPortfolioData';

/**
 * Growth Opportunities > Product Adoption > My Program Adoption — Partner
 * workspace, restricted to the enterprise_p1b and enterprise_lc plans (per
 * the mockup's "P1, LC" tag) — see the route gate in PrivateRouteWithLayout.js
 * and the nav row's visibleWhen in partnerWorkspace.nav.js.
 *
 * Built from a reference mockup. Program completion comes from the Partner
 * Dashboard's S_20; the usage chart from the partner workspace (PW) document's
 * program_adoption.usage_by_quarter (the last 4 quarters, actual module counts).
 * Colors/shapes follow this app's own tokens (orange progress fill,
 * plainCardSx-style cards), not the mockup's own navy/blue styling.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';
const GREEN = '#1B6B55';

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};


const CompletionBar = ({ label, pct }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography sx={{ fontSize: '13.5px', color: TEXT, width: { xs: '110px', sm: '150px' }, flexShrink: 0 }}>
            {label}
        </Typography>
        <Box sx={{ flex: 1, height: '10px', borderRadius: '50px', backgroundColor: 'var(--brand-bg, #F3F4F6)', overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: '50px', backgroundColor: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT, width: '44px', textAlign: 'right', flexShrink: 0 }}>
            {pct}%
        </Typography>
    </Box>
);

const MyProgramAdoptionView = ({ completionRows, usageData }) => (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
        <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
            My Program Adoption
        </Typography>
        <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 4 }}>
            Which programs are being used and completed?
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'stretch' }}>
            {/* Program completion */}
            <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT, mb: 3 }}>
                    Program completion (% of assigned)
                </Typography>
                <Box>
                    {completionRows.map((row) => (
                        <CompletionBar key={row.label} label={row.label} pct={row.pct} />
                    ))}
                </Box>
                <Typography sx={{ fontSize: '12px', color: MUTED, fontStyle: 'italic', mt: 1 }}>
                    Completion is activity, not impact — high completion does not by itself prove competency gain.
                </Typography>
            </Box>

            {/* Program usage across the year */}
            <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT, mb: 3 }}>
                    Program usage across the year
                </Typography>
                {usageData.length === 0 ? (
                    <Typography sx={{ fontSize: '13.5px', color: MUTED }}>
                        No program usage recorded yet.
                    </Typography>
                ) : (
                <Box sx={{ flexGrow: 1, minHeight: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={usageData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                            {/* Padding keeps the end labels ("Q4 2026") inside the chart. */}
                            <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: MUTED }} stroke={BORDER} padding={{ left: 24, right: 24 }} />
                            <YAxis tick={{ fontSize: 11, fill: MUTED }} stroke={BORDER} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="Modules started" stroke={ORANGE} strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="Modules completed" stroke={GREEN} strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
                )}
            </Box>
        </Box>
    </Box>
);

/**
 * LA-PW-20Oct-02: program completion is real — each deployed program and its
 * completion rate, from the Partner Dashboard's S_20 "Programs & deployment
 * status" table. The page itself is unchanged.
 */
const MyProgramAdoptionPage = () => {
    const { data } = usePartnerDashboardSections(['S_20']);
    const completionRows = programRowsFrom(data)
        .filter((r) => r.status === 'Deployed' && Number.isFinite(Number(r.rate)))
        .map((r) => ({ label: r.program, pct: Math.round(Number(r.rate)) }))
        .sort((x, y) => y.pct - x.pct);
    const { data: workspace } = usePartnerWorkspace();
    return <MyProgramAdoptionView completionRows={completionRows} usageData={usageByQuarterFrom(workspace)} />;
};

export default MyProgramAdoptionPage;
